import { TeamManagement } from "@/components/admin/TeamManagement";
import { UserManagement } from "@/components/admin/UserManagement";
import { ReportsManagement } from "@/components/admin/ReportsManagement";
import { ConversationsManagement } from "@/components/admin/ConversationsManagement";
import { CopilotAnalytics } from "@/components/admin/CopilotAnalytics";
import { PageShell } from "@/components/layout/PageShell";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertTriangle,
  ArrowUpRight,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  MessageSquare,
  Settings,
  Shield,
  Sparkles,
  Target,
  TrendingUp,
  Users
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useReports } from "@/hooks/useReports";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { adminService } from "@/services/adminService";

const Admin = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [showStats, setShowStats] = useState(true);
  const { reports: recentReports, loading: loadingReports } = useReports(1, 5);
  const { isAdmin } = useAuth();

  // Fetch Admin Overview Stats
  const { data: overviewData } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => adminService.getOverview(),
  });

  const stats = [
    { 
      label: "Total Priorities", 
      value: overviewData?.totalPriorities?.toString() || "0", 
      icon: Target 
    },
    { 
      label: "Active Team Members", 
      value: overviewData?.totalActiveUsers?.toString() || "0", 
      icon: Users 
    },
    { 
      label: "Reports Today", 
      value: overviewData?.totalReportsToday?.toString() || "0", 
      icon: FileText 
    },
    { 
      label: "Blockers Today", 
      value: overviewData?.totalBlockerReportsToday?.toString() || "0", 
      icon: AlertTriangle 
    },
  ];


  return (
    <PageShell>
      {/* Header */}
      <Collapsible open={showStats} onOpenChange={setShowStats}>
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-background to-secondary/5 border border-primary/10 p-8 shadow-sm">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <Shield className="w-64 h-64 -mt-16 -mr-16" />
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2.5 rounded-xl bg-primary/20 text-primary">
                  <Shield className="h-6 w-6" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
              </div>
              <p className="text-muted-foreground max-w-xl">
                Manage priorities, team, and view organization insights
              </p>
            </div>
            <div className="flex shrink-0 gap-2 flex-wrap">
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="gap-2 bg-background/60">
                  {showStats ? (
                    <><ChevronUp className="h-4 w-4" />Hide Stats</>
                  ) : (
                    <><ChevronDown className="h-4 w-4" />Show Stats</>
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
        </div>

        {/* Stats Grid - Collapsible */}
        <CollapsibleContent className="mb-8 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-200">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => (
              <Card key={stat.label} className="overflow-hidden hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                      <stat.icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-3xl font-bold">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="mb-6 flex w-full grid-cols-4 lg:w-auto lg:grid-cols-none">
          <TabsTrigger value="overview" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="team" className="gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Teams</span>
          </TabsTrigger>
          <TabsTrigger value="staff" className="gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Staff</span>
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Reports</span>
          </TabsTrigger>
          <TabsTrigger value="conversations" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Conversations</span>
          </TabsTrigger>
          <TabsTrigger value="copilot" className="gap-2">
            <Bot className="h-4 w-4" />
            <span className="hidden sm:inline">Copilot</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Settings</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Recent Reports */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Recent Reports</CardTitle>
                  <CardDescription>Today's daily report submissions</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1"
                  onClick={() => setActiveTab("reports")}
                >
                  View all
                  <ArrowUpRight className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loadingReports ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div className="space-y-1.5">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </div>
                          <Skeleton className="h-5 w-5 rounded-full" />
                        </div>
                      ))}
                    </div>
                  ) : recentReports.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="rounded-full bg-muted p-3 mb-3">
                        <CheckCircle2 className="h-6 w-6 text-muted-foreground/50" />
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">No reports today yet</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">Reports will appear as team members submit them.</p>
                    </div>
                  ) : (
                    recentReports.map((report) => (
                      <div key={report.pid} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            src={report.user?.avatarUrl}
                            name={report.user?.fullName}
                            className="h-10 w-10 border"
                          />
                          <div>
                            <p className="font-medium">{report.user?.fullName || "Unknown User"}</p>
                            <p className="text-sm text-muted-foreground">{format(new Date(report.reportDate), "MMM dd, HH:mm")}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {report.blocker && (
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-warning/10">
                              <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                            </span>
                          )}
                          <CheckCircle2 className="h-5 w-5 text-success" />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common administrative tasks</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3">
                <Link to="/priorities">
                  <Button variant="outline" className="h-auto w-full justify-start gap-3 p-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Target className="h-5 w-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">Manage Priorities</p>
                      <p className="text-sm text-muted-foreground">Create and edit SHPs and WKRs</p>
                    </div>
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="h-auto w-full justify-start gap-3 p-4"
                  onClick={() => setActiveTab("team")}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-info/10">
                    <Users className="h-5 w-5 text-info" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">Team Management</p>
                    <p className="text-sm text-muted-foreground">Add or manage team members</p>
                  </div>
                </Button>
                <Button variant="outline" className="h-auto w-full justify-start gap-3 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                    <Sparkles className="h-5 w-5 text-accent" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">AI Configuration</p>
                    <p className="text-sm text-muted-foreground">Configure AI model and prompts</p>
                  </div>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="team">
          <TeamManagement
            isAdmin={isAdmin}
          />
        </TabsContent>

        <TabsContent value="staff">
          <UserManagement />
        </TabsContent>

        <TabsContent value="reports">
          <ReportsManagement />
        </TabsContent>

        <TabsContent value="conversations">
          <ConversationsManagement />
        </TabsContent>

        <TabsContent value="copilot">
          <CopilotAnalytics />
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
              <CardDescription>Configure your organization and AI settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-xl border border-border p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">AI Configuration</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Configure AI models, system prompts, and behavior settings
                    </p>
                    <Button variant="outline" size="sm" className="mt-4">
                      Configure AI
                    </Button>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-border p-6">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10">
                    <Shield className="h-6 w-6 text-accent" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Security & Access</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Manage authentication, roles, and permissions
                    </p>
                    <Button variant="outline" size="sm" className="mt-4" onClick={() => setActiveTab("staff")}>
                      Manage Access
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PageShell>
  );
};

export default Admin;
